"use client";

import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ElementType,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from "react";
import { useState } from "react";
import { LuCheck, LuCopy } from "react-icons/lu";

import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { copyTextToClipboard } from "@/utils/helpers";
import { cn } from "@/utils/lib";

type AsProp<C extends ElementType> = {
  as?: C;
};

type PropsToOmit<C extends ElementType, P> = keyof (AsProp<C> & P);

type PolymorphicComponentProp<
  C extends ElementType,
  Props = NonNullable<unknown>,
> = PropsWithChildren<Props & AsProp<C>> &
  Omit<ComponentPropsWithoutRef<C>, PropsToOmit<C, Props>>;

type PolymorphicComponentPropWithRef<
  C extends ElementType,
  Props = NonNullable<unknown>,
> = PolymorphicComponentProp<C, Props> & { ref?: PolymorphicRef<C> };

type PolymorphicRef<C extends ElementType> = ComponentPropsWithRef<C>["ref"];

type CopyToClipboardProps<C extends ElementType> =
  PolymorphicComponentPropWithRef<
    C,
    {
      children?: ReactNode;
      className?: string;
      text: string;
      showIcon?: boolean;
      showToast?: boolean;
      iconSize?: number;
      handleCopyCallback?: (state: boolean) => void;
      iconClassName?: string;
      toastText?: string;
      iconPosition?: "left" | "right";
      as?: C;
    }
  >;

type CopyToClipboardComponent = <C extends ElementType = "div">(
  props: CopyToClipboardProps<C>,
) => ReactElement<unknown> | null;

const CHANGE_ICON_TIME_MS = 1500;

const CopyToClipboard: CopyToClipboardComponent = ({
  children,
  className = "",
  text,
  showIcon = true,
  showToast = false,
  iconSize = 16,
  iconClassName = "",
  toastText = "",
  handleCopyCallback,
  iconPosition = "left",
  as: Component = "div",
  ...rest
}) => {
  const { toast } = useToast();

  const [isCopied, setIsCopied] = useState(false);

  const handleCopyClick = async (copyText: string) => {
    try {
      await copyTextToClipboard(copyText);

      setIsCopied(true);

      if (showToast) {
        toast({
          variant: "success",
          title: "Success",
          duration: 2500,
          description: toastText || `Successfully copied to your clipboard`,
        });
      }

      handleCopyCallback?.(true);

      setTimeout(() => {
        setIsCopied(false);
        handleCopyCallback?.(false);
      }, CHANGE_ICON_TIME_MS);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when copying the text`,
        details: createToastErrorDetails(err),
      });

      return Promise.reject(err);
    }
  };

  const icon = isCopied ? (
    <LuCheck size={iconSize} className={cn("text-green-500", iconClassName)} />
  ) : (
    <LuCopy size={iconSize} className={cn("text-muted", iconClassName)} />
  );

  return (
    <Component
      onClick={() => handleCopyClick(text)}
      className={cn(`flex cursor-pointer items-center gap-2`, className)}
      {...rest}
    >
      {showIcon && iconPosition === "left" && icon}
      {children}
      {showIcon && iconPosition === "right" && icon}
    </Component>
  );
};

export default CopyToClipboard;
